import { announce } from "@kovernow/flows/announce";
import { toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import { acceptFor, createUploadQueue, type UploadKind } from "@kovernow/flows/uploads";
import {
  documentsOf,
  mergeDocuments,
  ownStorageLocation,
  withDocuments,
  type ItemDocument,
  type VaultItem,
} from "@kovernow/flows/vault";
import { Button } from "@kovernow/react-ui/components/button";
import { UploadField } from "@kovernow/react-ui/components/upload-field";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useRef, useState, type RefObject } from "react";
import { GatedButton } from "../../components/GatedButton";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { BusyLine, PipelineStatus, useUploadPipeline } from "../add-item/upload-pipeline";
import { storeUpdatedItem } from "./shared";

export const DOCUMENTS_HEADING_ID = "item-documents-heading";

/** Collector document groups, in the order the schemas list them. */
const COLLECTOR_TYPE_ORDER = [
  "invoice",
  "valuation_report",
  "provenance_records",
  "condition_report",
  "storage_statement",
  "proof_of_external_storage",
  "selfie_with_item",
];

/**
 * The item's documents. Legacy items: "Receipts" and "Selfies with item",
 * editable while uninsured or extensible (`manageable`): an add uploads
 * through the W-4 queue then sends `PUT /items` with the merged documents;
 * a remove sends `PUT /items` without it and deletes the Storage object
 * only after the 2xx. Nothing changes on screen before the server confirms.
 * Collector items: read-only, grouped by document type.
 */
export function ItemDocuments({
  item,
  name,
  collector,
  manageable,
  ownerId,
  headingRef,
}: {
  item: VaultItem;
  name: string;
  collector: boolean;
  manageable: boolean;
  ownerId: string | null;
  headingRef: RefObject<HTMLHeadingElement | null>;
}) {
  const t = useT();
  return (
    <section aria-labelledby={DOCUMENTS_HEADING_ID} className="flex flex-col gap-4" data-testid="item-documents">
      <h2 id={DOCUMENTS_HEADING_ID} ref={headingRef} tabIndex={-1} className="text-xl font-semibold outline-none">
        {t("itemDetail.documents.title")}
      </h2>
      {collector ? (
        <CollectorDocuments item={item} />
      ) : (
        <LegacyDocuments item={item} name={name} manageable={manageable} ownerId={ownerId} />
      )}
    </section>
  );
}

function FileLink({ doc, label }: { doc: ItemDocument; label: string }) {
  const t = useT();
  return (
    <a
      href={doc.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t("itemDetail.documents.newTab", { name: label })}
      className="inline-flex min-h-11 items-center gap-2 font-semibold text-primary underline-offset-4 hover:underline"
    >
      {label}
      <ExternalLink aria-hidden="true" className="size-4" />
    </a>
  );
}

function CollectorDocuments({ item }: { item: VaultItem }) {
  const t = useT();
  const category = item.classification ?? "";
  const docs = (item.documents ?? []).filter((d) => d.classification !== "item" && !!d.url);
  const types = [...new Set(docs.map((d) => d.type ?? d.classification))].sort(
    (a, b) => rank(a) - rank(b) || a.localeCompare(b),
  );
  if (!docs.length) {
    return <p className="text-muted-foreground">{t("itemDetail.documents.empty")}</p>;
  }
  return (
    <>
      {types.map((type) => {
        const group = docs
          .filter((d) => (d.type ?? d.classification) === type)
          .sort((a, b) => (a.order_index ?? Infinity) - (b.order_index ?? Infinity));
        return (
          <div key={type} className="flex flex-col gap-1" data-testid={`documents-${type}`}>
            <h3 className="font-semibold">{t([`collector.${category}.${type}`, "itemDetail.documents.title"])}</h3>
            <ul className="flex flex-col">
              {group.map((d, i) => (
                <li key={d.url}>
                  <FileLink doc={d} label={t(fileKey(d.classification), { n: i + 1 })} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <p className="text-sm text-muted-foreground">{t("itemDetail.documents.collectorReadOnly")}</p>
    </>
  );
}

function rank(type: string): number {
  const i = COLLECTOR_TYPE_ORDER.indexOf(type);
  return i < 0 ? COLLECTOR_TYPE_ORDER.length : i;
}

function fileKey(classification: string): string {
  if (classification === "document" || classification === "selfie" || classification === "item") {
    return `itemDetail.documents.file.${classification}`;
  }
  return "itemDetail.documents.file.other";
}

const SECTIONS: Array<{ classification: "document" | "selfie"; kind: UploadKind; titleKey: string; limitsKey: string }> = [
  { classification: "document", kind: "receipt", titleKey: "itemDetail.documents.receipts", limitsKey: "uploads.limits.receipt" },
  {
    classification: "selfie",
    kind: "selfie_with_item",
    titleKey: "itemDetail.documents.selfies",
    limitsKey: "uploads.limits.selfie_with_item",
  },
];

function LegacyDocuments({
  item,
  name,
  manageable,
  ownerId,
}: {
  item: VaultItem;
  name: string;
  manageable: boolean;
  ownerId: string | null;
}) {
  const t = useT();
  const services = useServices();
  const { vaultApi, config, uploads } = services;
  const queryClient = useQueryClient();
  const [queue] = useState(() => createUploadQueue({ adapter: uploads, convertHeic: services.convertHeic }));
  const inFlight = useRef(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeRecovery, setRemoveRecovery] = useState<{ action: RecoveryAction; url: string } | null>(null);

  const pipeline = useUploadPipeline({
    queue,
    ownerId,
    inFlight,
    createItem: async () => {
      const added = queue.documents();
      const updated = await vaultApi.update(
        withDocuments(item, mergeDocuments(item.documents ?? [], added), config.currency),
      );
      queue.clear();
      storeUpdatedItem(queryClient, updated);
      announce(t("itemDetail.announce.documentsAdded", { count: added.length, name }));
    },
    // No document code points at a field: every failure is a RecoveryView.
    onFieldError: () => undefined,
    errorFields: {},
  });
  const { qs, busy, locked, rowsFor, limitError, common } = pipeline;
  const queued = qs.files.some((f) => f.status !== "invalid");
  const upload = () => {
    if (queue.preparing()) return;
    void pipeline.run(() => queue.uploadPending(ownerId));
  };

  const remove = async (doc: ItemDocument, label: string) => {
    if (removing || locked) return;
    setRemoving(doc.url);
    setRemoveRecovery(null);
    announce(t("itemDetail.announce.removing", { doc: label }));
    try {
      const updated = await vaultApi.update(
        withDocuments(item, mergeDocuments(item.documents ?? [], [], new Set([doc.url])), config.currency),
      );
      storeUpdatedItem(queryClient, updated);
      // Storage only after the server confirmed; best effort, own prefix only.
      const location = ownStorageLocation(doc, ownerId);
      if (location) {
        try {
          await uploads.remove(ownerId, location);
        } catch {
          // An orphaned object is accepted.
        }
      }
      announce(t("itemDetail.announce.removed", { doc: label }));
    } catch (err) {
      setRemoveRecovery({ action: toRecoveryAction(err), url: doc.url });
    } finally {
      setRemoving(null);
    }
  };

  return (
    <>
      {SECTIONS.map((s) => {
        const docs = documentsOf(item, s.classification);
        return (
          <div key={s.classification} className="flex flex-col gap-2" data-testid={`documents-${s.classification}`}>
            <h3 className="font-semibold">{t(s.titleKey)}</h3>
            {docs.length ? (
              <ul className="flex flex-col divide-y rounded-lg border">
                {docs.map((d, i) => {
                  const label = t(fileKey(s.classification), { n: i + 1 });
                  return (
                    <li key={d.url} className="flex flex-col gap-2 px-4 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <FileLink doc={d} label={label} />
                        {manageable ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="touch"
                            aria-disabled={removing || locked ? true : undefined}
                            onClick={() => void remove(d, label)}
                          >
                            {removing === d.url ? t("common.loading") : t("itemDetail.documents.remove", { name: label })}
                          </Button>
                        ) : null}
                      </div>
                      {removeRecovery?.url === d.url ? (
                        <div data-testid="remove-document-error">
                          <Recovery action={removeRecovery.action} onRetry={() => void remove(d, label)} />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-muted-foreground">{t("itemDetail.documents.empty")}</p>
            )}
            {manageable ? (
              <UploadField
                {...common}
                testId={`upload-${s.kind}`}
                label={t(s.titleKey)}
                limits={t(s.limitsKey)}
                accept={acceptFor(s.kind)}
                capture={s.kind === "selfie_with_item" ? "user" : undefined}
                rows={rowsFor(s.kind)}
                error={limitError(s.kind)}
                onFiles={(files) => void queue.add(s.kind, files)}
              />
            ) : null}
          </div>
        );
      })}
      {manageable ? (
        <>
          <PipelineStatus uploadsFailed={pipeline.uploadsFailed} recovery={pipeline.recovery} onRetry={upload} />
          {queued ? (
            <div className="sm:max-w-56">
              <GatedButton
                variant="cta"
                label={t("itemDetail.documents.upload")}
                busy={locked}
                unavailableReason={queue.preparing() ? t("common.loading") : undefined}
                onClick={upload}
              />
            </div>
          ) : null}
          <BusyLine busy={busy} />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{t("itemDetail.documents.readOnly")}</p>
      )}
    </>
  );
}
